import { useState, useEffect, useMemo } from 'react';
import { Button, Switch } from 'antd';
import PriceTag from 'components/price-tag/v3';
import { PriceTagInfo } from 'types/pms';
import i18n from 'i18n';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
// https://react-dnd.github.io/react-dnd/examples/dustbin/multiple-targets
import Container from 'components/price-tag/v3/container';
import lodash from 'lodash';
import classNames from 'classnames';
import styles from './index.module.scss';
import { getProductPassportUrlList } from 'apis/pms';

const PRINT_MARGIN_STORAGE_KEY = 'price-tag-print-has-margin';
const SHOW_ASSET_QR_CODE_STORAGE_KEY = 'price-tag-print-show-asset-qr-code';
const SHOW_FULL_STAMP_STORAGE_KEY = 'price-tag-print-show-full-stamp';

/** 仅本页挂载期间生效，卸载后移除，避免影响合同等需要多页 A4 的打印 */
const PRICE_TAG_PRINT_PAGE_STYLE = `
@media print {
  @page {
    size: A4 portrait;
    margin: 0;
  }
  html, body, #root {
    width: 210mm !important;
    height: 297mm !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: hidden !important;
    background: none !important;
  }
}
`;

const PriceTagPrint = () => {
  const [state, setState] = useState<PriceTagInfo[]>([]);
  const [isDragging, setDragging] = useState<boolean>(false);
  const [hasMargin, setHasMargin] = useState<boolean>(true);
  const [showAssetQrCode, setShowAssetQrCode] = useState<boolean>(true);
  const [showFullStamp, setShowFullStamp] = useState<boolean>(false);
  const [passportUrlMap, setPassportUrlMap] = useState<Record<number, string>>(
    {},
  );

  // Stable string key: only changes when the set of product IDs changes, not on drag-reorder
  const sortedIdsKey = useMemo(
    () => [...state.map((d) => d.id)].sort((a, b) => a - b).join(','),
    [state],
  );

  useEffect(() => {
    const style = document.createElement('style');
    style.dataset.priceTagPrint = 'true';
    style.textContent = PRICE_TAG_PRINT_PAGE_STYLE;
    document.head.appendChild(style);
    return () => {
      style.remove();
    };
  }, []);

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem('price-tag') || '[]');
    setState(data);

    const savedHasMargin = localStorage.getItem(PRINT_MARGIN_STORAGE_KEY);
    if (savedHasMargin !== null) {
      setHasMargin(savedHasMargin === 'true');
    }

    const savedShowAssetQrCode = localStorage.getItem(
      SHOW_ASSET_QR_CODE_STORAGE_KEY,
    );
    if (savedShowAssetQrCode !== null) {
      setShowAssetQrCode(savedShowAssetQrCode === 'true');
    }

    const savedShowFullStamp = localStorage.getItem(
      SHOW_FULL_STAMP_STORAGE_KEY,
    );
    if (savedShowFullStamp !== null) {
      setShowFullStamp(savedShowFullStamp === 'true');
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('printData', JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    localStorage.setItem(PRINT_MARGIN_STORAGE_KEY, String(hasMargin));
  }, [hasMargin]);

  useEffect(() => {
    localStorage.setItem(
      SHOW_ASSET_QR_CODE_STORAGE_KEY,
      String(showAssetQrCode),
    );
  }, [showAssetQrCode]);

  useEffect(() => {
    localStorage.setItem(SHOW_FULL_STAMP_STORAGE_KEY, String(showFullStamp));
  }, [showFullStamp]);

  useEffect(() => {
    if (!sortedIdsKey) return;
    const ids = sortedIdsKey.split(',').map(Number);
    getProductPassportUrlList(ids)
      .then((res) => {
        const map = res.reduce(
          (acc, item) => {
            if (item.passportUrl) acc[item.productId] = item.passportUrl;
            return acc;
          },
          {} as Record<number, string>,
        );
        setPassportUrlMap(map);
      })
      .catch(() => {});
  }, [sortedIdsKey]);

  /** 拖动数据处理 */
  const handleDropNew = (
    item: PriceTagInfo,
    index: number,
    data: PriceTagInfo,
  ) => {
    const currIndex: number = state.findIndex((d) => d.id === data.id);
    const list = lodash.clone(state);
    list[index] = data;
    list[currIndex] = item;
    setState(list);
  };

  return (
    <div
      className={classNames(
        styles.page,
        'flex flex-col items-center h-screen justify-center',
      )}
    >
      <div
        // 纸张型号 KPC-E1241-20 https://www.kokuyo-st.co.jp/stationery/hakadorilabel/kyoutsuu/
        className={classNames(
          'w-[29.7cm] h-[21cm] bg-white relative box-border',
          hasMargin ? 'px-[12.9mm]' : 'px-0',
          styles.printElement,
        )}
      >
        <div
          className={classNames(
            'h-full box-border grid grid-cols-8 grid-rows-3 gap-0 border border-black border-t-0 border-l-0',
            styles.noBorder,
          )}
        >
          <DndProvider backend={HTML5Backend}>
            {state.map((item, index) => {
              return (
                <Container
                  accept={['CONTENT']} // 允许拖入的类型
                  data={item}
                  onDrop={(data) => {
                    handleDropNew(item, index, data);
                  }}
                  key={index}
                  className={styles.noBorder}
                >
                  <PriceTag
                    key={item.id}
                    parentDragging={isDragging}
                    data={item}
                    isRef={true}
                    type={item.productSn ? 'CONTENT' : ''} // 拖入的类型
                    isDropped={false}
                    setDragging={setDragging}
                    passportUrl={
                      showAssetQrCode ? passportUrlMap[item.id] : undefined
                    }
                    showFullStamp={showFullStamp}
                  />
                </Container>
              );
            })}
          </DndProvider>
        </div>
      </div>

      <div
        className={classNames(
          styles.hideElement,
          'mt-2 flex flex-col items-center gap-2',
        )}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-black">
            <span>{i18n.t('has_blank_margin')}</span>
            <Switch
              aria-label={i18n.t('has_blank_margin')}
              checked={hasMargin}
              onChange={setHasMargin}
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-black">
            <span>{i18n.t('show_asset_qr_code')}</span>
            <Switch
              aria-label={i18n.t('show_asset_qr_code')}
              checked={showAssetQrCode}
              onChange={setShowAssetQrCode}
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-black">
            <span>{i18n.t('show_full_stamp')}</span>
            <Switch
              aria-label={i18n.t('show_full_stamp')}
              checked={showFullStamp}
              onChange={setShowFullStamp}
            />
          </div>
        </div>
        <Button
          type="primary"
          onClick={() => {
            window.print();
          }}
        >
          {i18n.t('print')}
        </Button>
      </div>
    </div>
  );
};

export default PriceTagPrint;
